import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";

const isDescriptionEmpty = (value) => !value || !value.trim();

const defaultFormFromRequest = (req) => ({
  title: req?.title || "",
  description: req?.description || "",
  priority: req?.priority || "Medium",
});

export default function EditRequestModal({
  isOpen,
  onClose,
  request,
  refreshList,
}) {
  const [form, setForm] = useState(defaultFormFromRequest(request));
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  useEffect(() => {
    if (isOpen) {
      setForm(defaultFormFromRequest(request));
      setFeedback({ type: "", message: "" });
    }
  }, [isOpen, request]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));

    if (feedback.message) {
      setFeedback({ type: "", message: "" });
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!request?.id) return;

      if (!form.title.trim()) {
        setFeedback({
          type: "error",
          message: "Le titre est requis.",
        });
        return;
      }

      if (isDescriptionEmpty(form.description)) {
        setFeedback({
          type: "error",
          message: "La description est requise.",
        });
        return;
      }

      setLoading(true);
      setFeedback({ type: "", message: "" });

      try {
        const payload = {
          title: form.title.trim(),
          description: form.description.trim(),
          priority: form.priority,
        };

        await axios.patch(
          `https://api.monappliclub.com/api/requests/updateRequest/${request.id}`,
          payload
        );

        refreshList?.();
        onClose?.();
      } catch (error) {
        console.error("Erreur lors de la mise à jour de la requête :", error);
        setFeedback({
          type: "error",
          message: "Erreur lors de la mise à jour de la requête.",
        });
      } finally {
        setLoading(false);
      }
    },
    [form, onClose, refreshList, request?.id]
  );

  if (!isOpen || !request) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/70 dark:bg-slate-900 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-center text-lg font-bold uppercase text-white sm:text-xl">
            Modifier la requête
          </h3>
          <p className="mt-1 text-center text-xs text-white/90">
            Ajustez le titre, la description et la priorité. Appuyez sur Échap
            pour fermer.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5 sm:p-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-800 dark:text-slate-100">
              Titre
            </label>
            <input
              type="text"
              value={form.title}
              onChange={handleChange("title")}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-800 dark:text-slate-100">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={handleChange("description")}
              rows={10}
              placeholder="Détaillez la demande, le contexte, les étapes, le comportement observé et attendu…"
              className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm leading-6 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              required
            />
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Conseil : indiquez les étapes pour reproduire et l’environnement
              utilisé.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-800 dark:text-slate-100">
              Priorité
            </label>
            <select
              value={form.priority}
              onChange={handleChange("priority")}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="High">Urgent</option>
              <option value="Medium">Moyenne</option>
              <option value="Low">Basse</option>
            </select>
          </div>

          {feedback.message && (
            <div
              className={`rounded-xl border px-3 py-2 text-sm ${
                feedback.type === "error"
                  ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
              }`}
            >
              {feedback.message}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!rounded-xl !bg-slate-100 !text-slate-800 gap-2 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
              disabled={loading}
            />
            <Button
              text={loading ? "En cours…" : "Modifier"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${
                loading ? "pointer-events-none opacity-60" : ""
              }`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}